import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, inr, nfmt, fmtDate, fmtDateTime, downloadCSV } from "./utils";
import { ArrowLeft, DownloadSimple, User, Receipt, CurrencyInr, PaperPlaneTilt, ClockCounterClockwise } from "@phosphor-icons/react";

const KPI = ({ label, value, sub, accent = "#FFFFFF", testid }) => (
  <div data-testid={testid} className="nb-card p-4" style={{ background: accent }}>
    <div className="text-[10px] font-bold uppercase tracking-[0.2em]">{label}</div>
    <div className="font-heading font-black text-2xl tracking-tighter mt-1">{value}</div>
    {sub && <div className="text-[11px] text-[#4A4A4A] mt-1">{sub}</div>}
  </div>
);

export default function AdminBusinessDetail() {
  const { user_id } = useParams();
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get(`/admin/businesses/${user_id}`),
          api.get(`/admin/businesses/${user_id}/timeline?limit=80`),
        ]);
        setData(a.data);
        setTimeline(b.data.events || []);
      } catch (e) { setErr(e?.response?.data?.detail || "Failed to load business"); }
    })();
  }, [user_id]);

  if (err) return <div className="nb-card p-4 bg-[#FF6B6B] text-white font-bold">{err}</div>;
  if (!data) return <div className="p-4 text-sm">Loading…</div>;

  const a = data.account; const m = data.metrics; const r = data.recent;

  return (
    <div className="space-y-6" data-testid="admin-biz-detail">
      <Link to="/admin/businesses" data-testid="back-to-list" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider">
        <ArrowLeft size={14} weight="bold" /> Back to businesses
      </Link>

      {/* Account header */}
      <div className="nb-card p-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {a.picture ? <img src={a.picture} alt="" className="w-14 h-14 rounded-md border-2 border-black object-cover" /> :
            <div className="w-14 h-14 rounded-md border-2 border-black bg-[#FFD166] flex items-center justify-center"><User size={22} weight="bold" /></div>}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#4A4A4A]">Business account</div>
            <h2 className="font-heading font-black text-2xl tracking-tight leading-none">{a.business_name || a.name}</h2>
            <div className="text-[13px] text-[#4A4A4A] mt-1">{a.email} · {a.business_type || "—"}</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider bg-[#F3F0E9] border border-black/10 rounded px-2 py-0.5">Plan: {a.plan}</span>
          <span className={`text-[10px] font-bold uppercase tracking-wider border rounded px-2 py-0.5 ${a.status === "active" ? "bg-[#4ECDC4]/30 border-[#4ECDC4]" : "bg-white border-black/10 text-[#4A4A4A]"}`}>{a.status}</span>
          <span className="text-[11px] text-[#4A4A4A]">Since {fmtDate(a.created_at)}</span>
          <button
            data-testid="export-invoices"
            onClick={() => downloadCSV("invoices", { user_id: a.user_id })}
            className="nb-btn bg-white text-xs px-3 py-1.5 flex items-center gap-1"
          ><DownloadSimple size={12} weight="bold" /> Invoices CSV</button>
          <button
            data-testid="export-payments"
            onClick={() => downloadCSV("payments", { user_id: a.user_id })}
            className="nb-btn bg-white text-xs px-3 py-1.5 flex items-center gap-1"
          ><DownloadSimple size={12} weight="bold" /> Payments CSV</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI label="End Customers" value={nfmt(m.total_customers)} icon={User} testid="detail-customers" />
        <KPI label="Invoices" value={nfmt(m.total_invoices)} sub={`${m.paid_count} paid`} testid="detail-invoices" />
        <KPI label="Invoiced" value={inr(m.total_invoiced)} accent="#FFD166" testid="detail-invoiced" />
        <KPI label="Collected" value={inr(m.total_paid)} accent="#4ECDC4" testid="detail-paid" />
        <KPI label="Outstanding" value={inr(m.total_outstanding)} testid="detail-outstanding" />
        <KPI label="Overdue Amount" value={inr(m.overdue_amount)} sub={`${m.overdue_count} invoices`} accent="#FF6B6B" testid="detail-overdue" />
        <KPI label="Reminders" value={nfmt(m.reminders_total)} sub={`${m.reminders_failed} failed`} testid="detail-reminders" />
        <KPI label="Last Activity" value={m.last_activity ? fmtDate(m.last_activity) : "—"} testid="detail-last-activity" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Recent invoices */}
        <div className="nb-card p-4">
          <h3 className="font-heading font-black text-lg mb-3 flex items-center gap-2"><Receipt size={18} weight="bold" /> Recent invoices</h3>
          {r.invoices.length === 0 ? <p className="text-sm text-[#4A4A4A]">No data available</p> : (
            <ul className="space-y-2" data-testid="recent-invoices">
              {r.invoices.map((i) => (
                <li key={i.invoice_id} className="flex items-center justify-between text-sm border-b border-black/10 pb-2">
                  <div>
                    <div className="font-bold">{i.invoice_number || "(no number)"}</div>
                    <div className="text-[11px] text-[#4A4A4A]">{i.customer_name} · Due {fmtDate(i.due_date)}</div>
                  </div>
                  <div className="font-bold">{inr(i.amount)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent payments */}
        <div className="nb-card p-4">
          <h3 className="font-heading font-black text-lg mb-3 flex items-center gap-2"><CurrencyInr size={18} weight="bold" /> Recent payments</h3>
          {r.payments.length === 0 ? <p className="text-sm text-[#4A4A4A]">No data available</p> : (
            <ul className="space-y-2" data-testid="recent-payments">
              {r.payments.map((p) => (
                <li key={p.payment_id} className="flex items-center justify-between text-sm border-b border-black/10 pb-2">
                  <div>
                    <div className="font-bold">{inr(p.amount)}</div>
                    <div className="text-[11px] text-[#4A4A4A]">{p.method} · {fmtDate(p.payment_date)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent reminders */}
        <div className="nb-card p-4">
          <h3 className="font-heading font-black text-lg mb-3 flex items-center gap-2"><PaperPlaneTilt size={18} weight="bold" /> Recent reminders</h3>
          {r.reminders.length === 0 ? <p className="text-sm text-[#4A4A4A]">No data available</p> : (
            <ul className="space-y-2" data-testid="recent-reminders">
              {r.reminders.map((rr) => (
                <li key={rr.reminder_id} className="text-sm border-b border-black/10 pb-2">
                  <div className="flex items-center justify-between">
                    <div className="font-bold">{rr.customer_name}</div>
                    <span className={`text-[10px] font-bold uppercase ${String(rr.status).toLowerCase().startsWith("failed") ? "text-[#FF6B6B]" : "text-[#4ECDC4]"}`}>{rr.status}</span>
                  </div>
                  <div className="text-[11px] text-[#4A4A4A]">{fmtDateTime(rr.sent_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Timeline */}
        <div className="nb-card p-4">
          <h3 className="font-heading font-black text-lg mb-3 flex items-center gap-2"><ClockCounterClockwise size={18} weight="bold" /> Activity timeline</h3>
          {timeline.length === 0 ? <p className="text-sm text-[#4A4A4A]">No data available</p> : (
            <ul className="space-y-2 max-h-96 overflow-y-auto" data-testid="timeline">
              {timeline.map((ev, idx) => (
                <li key={idx} className="text-sm border-b border-black/10 pb-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{ev.label}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A]">{ev.type.replace("_", " ")}</span>
                  </div>
                  <div className="text-[11px] text-[#4A4A4A]">{fmtDateTime(ev.at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
